import { motion } from "framer-motion";
import { theme } from "@/theme";

interface SectionTitleProps {
  /** Optional label above the title */
  label?: string;
  
  /** Main title text */
  title: string;
  
  /** Accent word(s) to highlight - will be colored with theme accent */
  accent?: string;
  
  /** Optional subtitle/description */
  subtitle?: string;
  
  /** Show animated underline beneath accent word */
  showUnderline?: boolean;
  
  /** Text alignment */
  align?: "left" | "center" | "right";
  
  /** Size variant */
  size?: "sm" | "md" | "lg" | "xl";
  
  /** Additional CSS classes */
  className?: string;
  
  /** Animation delay */
  delay?: number;
}

const SectionTitle: React.FC<SectionTitleProps> = ({
  label,
  title,
  accent,
  subtitle,
  showUnderline = true,
  align = "center",
  size = "lg",
  className = "",
  delay = 0.1,
}) => {
  const sizeClasses = {
    sm: "text-3xl md:text-4xl",
    md: "text-4xl md:text-5xl",
    lg: "text-4xl md:text-6xl",
    xl: "text-5xl md:text-7xl",
  };
  
  const subtitleSizeClasses = {
    sm: "text-sm md:text-base",
    md: "text-base md:text-lg",
    lg: "text-lg md:text-xl",
    xl: "text-xl md:text-2xl",
  };
  
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };
  
  const alignItems = {
    left: "items-start",
    center: "items-center",
    right: "items-end",
  };
  
  // Split title by accent word if provided
  const titleParts = accent 
    ? title.split(accent)
    : [title];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{
        duration: 0.6,
        delay: delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={`flex flex-col ${alignItems[align]} ${className}`}
    >
      {/* Optional Label Badge */}
      {label && (
        <motion.div 
          className="inline-block px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wider uppercase"
          style={{
            backgroundColor: `${theme.colors.accent}20`,
            color: theme.colors.accent,
            border: `1px solid ${theme.colors.accent}40`,
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: delay }}
        >
          {label}
        </motion.div>
      )}

      {/* Main Title */}
      <h2
        className={`${sizeClasses[size]} font-bold mb-6 leading-tight ${alignClasses[align]}`}
        style={{ color: theme.colors.text }}
      >
        {accent ? (
          <>
            {titleParts[0]}
            <span 
              className="inline-block relative mx-2"
              style={{ color: theme.colors.accent }}
            >
              {accent}
              {showUnderline && (
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ backgroundColor: theme.colors.accent }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: delay + 0.4 }}
                />
              )}
            </span>
            {titleParts[1]}
          </>
        ) : (
          title
        )}
      </h2>

      {/* Optional Subtitle */}
      {subtitle && (
        <motion.p
          className={`${subtitleSizeClasses[size]} leading-relaxed max-w-3xl ${alignClasses[align]}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.5,
            delay: delay + 0.2,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          style={{ color: theme.colors.secondary }}
        >
          {subtitle}
        </motion.p>
      )}
    </motion.div>
  );
};

export default SectionTitle;

